import * as React from "react";
import {
  Spinner,
  Toast,
  ToastBody,
  ToastTitle,
  Toaster,
  useId,
  useToastController,
} from "@fluentui/react-components";
import {
  FieldRow,
  LabeledCheckbox,
  LabeledInput,
  LabeledSelect,
  SectionCard,
} from "../../components/forms/formPrimitives";
import { REFINED } from "../../components/risikoanalyseStep/tokens";
import type { ProfileSectionProps } from "./profileShell";
import {
  CADENCE_OPTIONS,
  intervalUnitLabel,
  isIntervalCadence,
  type NotificationCadence,
} from "./notificationCadence";
import {
  useNotificationPreferences,
  type NotificationRow,
  type NotifyIntent,
} from "./useNotificationPreferences";

/**
 * Sektion „Benachrichtigungen" der Profil-Seite (SP5).
 *
 * Zeigt je zugewiesenem Postfach eine Zeile mit Kadenz-Auswahl (Sofort / Alle 2
 * Stunden / Alle X Stunden / Alle X Minuten), einem bedingten Intervall-Feld und
 * einem Aktiv-Schalter. Speichert optimistisch (PUT pro Änderung) mit Rollback +
 * Toast bei Fehler. Geht LIVE, weil der Digest-Scheduler (SP4) bereitsteht — alle
 * Kadenz-Optionen wirken jetzt wirklich (E2 erfüllt).
 *
 * `saving`-Zustand wird über ein umschließendes `<fieldset disabled>` realisiert,
 * weil `LabeledSelect`/`LabeledInput` KEIN `disabled`-Prop tragen (Konsistenz P2).
 */

const SAVING_FIELDSET: React.CSSProperties = {
  border: "none",
  margin: 0,
  padding: 0,
  minWidth: 0,
  display: "block",
};

const NotificationRowView: React.FC<{
  row: NotificationRow;
  onCadence: (address: string, cadence: NotificationCadence) => void;
  onInterval: (address: string, value: number | null) => void;
  onEnabled: (address: string, enabled: boolean) => void;
}> = ({ row, onCadence, onInterval, onEnabled }) => {
  const address = row.mailboxAddress;
  const showInterval = isIntervalCadence(row.cadence);
  const unit = intervalUnitLabel(row.cadence);

  return (
    <FieldRow columns={2}>
      {/* Spalte 1: Postfach-Label + Adresse + Aktiv-Schalter. */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span
            style={{
              fontFamily: REFINED.fontUI,
              fontWeight: 600,
              fontSize: "0.95rem",
              color: REFINED.ink,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={row.displayLabel}
          >
            {row.displayLabel}
          </span>
          {row.saving && <Spinner size="tiny" aria-label="Speichern" />}
        </div>
        {row.displayLabel !== address && (
          <span
            style={{
              fontFamily: REFINED.fontUI,
              fontSize: "0.8rem",
              color: REFINED.ink2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={address}
          >
            {address}
          </span>
        )}
        <LabeledCheckbox
          label="Benachrichtigungen aktiv"
          checked={row.enabled}
          onChange={(b) => onEnabled(address, b)}
          disabled={row.saving}
        />
      </div>

      {/* Spalte 2: Kadenz-Select + bedingtes Intervall-Feld. Beim Speichern via
          fieldset deaktiviert (LabeledSelect/LabeledInput haben kein disabled). */}
      <fieldset style={SAVING_FIELDSET} disabled={row.saving}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <LabeledSelect
            label="Benachrichtigung"
            name={`cad-${address}`}
            value={row.cadence}
            options={CADENCE_OPTIONS as Array<{ value: string; label: string }>}
            onChange={(v) => onCadence(address, v as NotificationCadence)}
          />
          {showInterval && (
            <LabeledInput
              label={`Intervall${unit ? ` (${unit})` : ""}`}
              name={`int-${address}`}
              type="number"
              value={row.intervalValue == null ? "" : String(row.intervalValue)}
              onChange={(v) =>
                onInterval(address, v === "" ? null : Number(v))
              }
              placeholder={unit === "Stunden" ? "1 – 24" : "1 – 1440"}
            />
          )}
        </div>
      </fieldset>
    </FieldRow>
  );
};

export const ProfileNotifications: React.FC<ProfileSectionProps> = () => {
  const toasterId = useId("profile-notif-toaster");
  const { dispatchToast } = useToastController(toasterId);

  const notify = React.useCallback(
    (message: string, intent: NotifyIntent) => {
      dispatchToast(
        <Toast>
          <ToastTitle>
            {intent === "error" ? "Fehler" : "Gespeichert"}
          </ToastTitle>
          <ToastBody>{message}</ToastBody>
        </Toast>,
        { intent },
      );
    },
    [dispatchToast],
  );

  const { rows, loading, loadError, setCadence, setInterval, setEnabled, reload } =
    useNotificationPreferences(notify);

  let body: React.ReactNode;
  if (loading) {
    body = (
      <div style={{ display: "flex", justifyContent: "center", padding: "12px 0" }}>
        <Spinner size="small" label="Laden …" />
      </div>
    );
  } else if (loadError) {
    body = (
      <div style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "flex-start" }}>
        <p style={{ margin: 0, fontFamily: REFINED.fontUI, color: REFINED.err, fontSize: "0.9rem" }}>
          {loadError}
        </p>
        <button
          type="button"
          onClick={reload}
          style={{
            appearance: "none",
            cursor: "pointer",
            fontFamily: REFINED.fontUI,
            fontSize: "0.875rem",
            fontWeight: 600,
            color: REFINED.white,
            background: REFINED.ink,
            border: "none",
            borderRadius: 10,
            padding: "9px 16px",
          }}
        >
          Erneut laden
        </button>
      </div>
    );
  } else if (rows.length === 0) {
    body = (
      <p style={{ margin: 0, fontFamily: REFINED.fontUI, color: REFINED.ink2, fontSize: "0.95rem" }}>
        Dir sind aktuell keine Postfächer zugewiesen.
      </p>
    );
  } else {
    body = (
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <p style={{ margin: 0, fontFamily: REFINED.fontUI, color: REFINED.ink2, fontSize: "0.9rem" }}>
          Lege je Postfach fest, wie häufig du über neue E-Mails benachrichtigt wirst.
          Mail-Benachrichtigungen sind <strong>standardmäßig deaktiviert</strong> und müssen
          pro Postfach aktiviert werden.
        </p>
        {rows.map((row, idx) => (
          <div
            key={row.mailboxAddress}
            style={{
              paddingTop: idx === 0 ? 0 : 16,
              borderTop: idx === 0 ? "none" : `1px solid ${REFINED.line}`,
            }}
          >
            <NotificationRowView
              row={row}
              onCadence={setCadence}
              onInterval={setInterval}
              onEnabled={setEnabled}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <SectionCard title="BENACHRICHTIGUNGEN" heading="Benachrichtigungen">
      {body}
      <Toaster toasterId={toasterId} position="top-end" />
    </SectionCard>
  );
};

export default ProfileNotifications;
